// Ported verbatim in behaviour (WS-D §2) — wraps a manifest entry's lazy `loader` in
// `React.lazy` + `Suspense`. The entries are bundled in the same build, so loading is a single
// dynamic `import()` per screen.
//
// ⛔ THE lazy() COMPONENT IS CACHED AT MODULE SCOPE, keyed by `entry.id`, and the two traps
// that forced that apply here identically:
//   1. If the AppLoader instance unmounts and remounts (React Router swaps the matched element
//      on a URL change), a `useMemo` cache is lost and a fresh `lazy()` is built. The new
//      wrapper calls `loader()` again, getting a NEW Promise reference (the inner ES module is
//      cached, but the Promise wrapping it is not). Suspense treats the new Promise as a fresh
//      suspension and renders the fallback for a tick — for a fast-resolving cached module this
//      shows as the old content being replaced by the fallback and then by either nothing or the
//      wrong thing, depending on commit timing.
//   2. If the entry prop reference changes between renders of the SAME instance — which happens
//      whenever `AppRegistryProvider` recomputes its visible lists on a permission-snapshot
//      churn — a `useMemo` invalidates and the same fragile re-lazy happens.
// Keying at module scope on a stable string makes each wrapper a process-lifetime singleton, so
// React.lazy's internal payload state survives mount/remount cycles.
import { Suspense, lazy, type ComponentType } from 'react';
import { Loader2 } from 'lucide-react';
import type { ShellEntry } from './manifest';
import { PaneFrame } from './PaneFrame';

export interface AppLoaderProps {
  readonly entry: ShellEntry;
}

/**
 * Process-wide cache of `lazy(loader)` keyed by `entry.id`.
 *
 * Vitest gives each test FILE a fresh module context, so this Map is naturally scoped per file
 * and never leaks between unrelated suites.
 */
const lazyByEntryId = new Map<string, ComponentType>();

function getOrCreateLazy(entry: ShellEntry): ComponentType {
  const cached = lazyByEntryId.get(entry.id);
  if (cached) return cached;
  const created = lazy(entry.loader);
  lazyByEntryId.set(entry.id, created);
  return created;
}

export function AppLoader({ entry }: AppLoaderProps): React.JSX.Element {
  const Lazy = getOrCreateLazy(entry);
  return (
    <Suspense
      fallback={
        <div data-testid="app-loader-fallback" className="flex flex-1 items-center justify-center">
          {/* `motion-reduce:animate-none` so a reduced-motion preference is honoured even on the
              transient loading indicator — a spinner is exactly the kind of thing that gets
              forgotten. */}
          <Loader2 className="h-6 w-6 animate-spin text-muted motion-reduce:animate-none" aria-hidden />
          <span className="sr-only">Loading</span>
        </div>
      }
    >
      {/*
        ⭐ THE ONE DOOR, so the page frame is structural rather than remembered. Every routed pane —
        global, detail (`AppHost.tsx:228-242`) and app-scoped (`AppScopeRoute`) — is rendered by this
        component, which makes this the only place `.page` (the deck's `tokens.css:296`) has to be
        applied. It used to be re-declared at the root of each pane, where fifteen said `p-8`, two
        said `p-6` and two said nothing at all and shipped edge-to-edge. See `PaneFrame.tsx`.

        ⛔ INSIDE `Suspense`, wrapping `<Lazy/>` ONLY — never around the whole `Suspense`. The
        fallback is a spinner centred in the full content region; framing it would inset it by the
        frame's 28px horizontal padding, so it would sit off-centre and then JUMP when the pane
        resolved. A loading indicator that moves on resolve reads as a layout bug, and
        `pane-frame.test.tsx` pins the fallback as unframed for this reason.

        ⚠️ This said "and cap it at 1400px" until 2026-08-06. The frame no longer caps width at all
        (an artboard size that left a fifth of a 2000px display empty — see `PaneFrame.tsx`), so the
        padding is now the whole reason. The stale half was corrected rather than left: a comment
        that still explains a removed mechanism outlives the change and misleads the next reader.
      */}
      <PaneFrame>
        <Lazy />
      </PaneFrame>
    </Suspense>
  );
}
